import type { FC, ComponentType, Dispatch, SetStateAction } from 'react';
import React, { Fragment, useState } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Flex, xcss, Box, Stack } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';

import { QueryInterop as Query } from '@confluence/apollo-relay-interop/entry-points/QueryInterop';
import {
	VIEW_PAGE_CONTENT_HEADER_EXPERIENCE,
	OBJECT_HEADER_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import {
	ExperienceSuccess,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component';
import { ContentTypesHeader } from '@confluence/content-types-header/entry-points/ContentTypesHeader';
import {
	Breadcrumbs,
	FixedSpaceHeaderContainer,
} from '@confluence/breadcrumbs/entry-points/Breadcrumbs';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import {
	CONTENT_METADATA_PAGE_RESTRICTIONS_ID,
	CONTENT_METADATA_ATTACHMENTS_ID,
} from '@confluence/web-item/entry-points/webItemIDs';
import { RendererContentState } from '@confluence/content-state/entry-points/RendererContentState';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator';
import { LoadableAfterPaint } from '@confluence/loadable';
import { useContentClassification } from '@confluence/data-classification/entry-points/queries';
import { usePageHeightWidthObserver } from '@confluence/page-cover-picture/entry-points/usePageHeightWidthObserver';
import { fg } from '@confluence/feature-gating';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';
import { ContentHeaderPresenceLoader } from '@confluence/team-presence/entry-points/ContentHeaderPresenceLoader';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';

import { ContentHeaderUnifiedQuery } from './ContentHeaderUnifiedQuery.graphql';
import type { ContentHeaderUnifiedQuery as ContentHeaderUnifiedQueryType } from './__types__/ContentHeaderUnifiedQuery';
import { DEFAULT_CONTRIBUTORS_FETCH_LIMIT } from './defaultQueryVariables';
import { PageMetadataAtl } from './PageMetadataAtl';
import { PageMetadataBanner } from './PageMetadataBanner';
import { SystemContentMetadata } from './SystemContentMetadata';
import { GroupedPageButtons } from './GroupedPageButtons';
import { CONNECT_APP_WEB_FRAGMENT_METRIC } from './perf.config';
import { checkIsFabricPage } from './content-title/ContentTitle';
import { SmartCreateFeedbackBannerLoader } from '@confluence/editor-action-bar/entry-points/SmartCreateFeedbackBannerLoader';

const ContentClassificationTagLoader = LoadableAfterPaint({
	name: 'ContentClassificationTag',
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-confluencedata-classificationentry-pointsContentClassificationTag" */ '@confluence/data-classification/entry-points/ContentClassificationTag'
			)
		).ContentClassificationTag,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpacedActionItem = styled.div({
	marginLeft: token('space.050'),
});

const breadcrumbsAreaWrapper = xcss({
	flexWrap: 'nowrap',
	flex: '0 1 auto',
	minWidth: 'fit-content',
	gap: 'space.050',
	overflow: 'hidden',

	[media.above.xs]: {
		minWidth: '96px',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const Metadata = styled.div((props: MetadataProps) => ({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'wrap',
	overflow: 'hidden', // needed to hide unpublished lozenge below certain header width
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: props.shouldDisplayClassification ? '20px' : '30px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& .content-metadata-jira': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		marginTop: props.shouldDisplayClassification ? '-5px' : '0',
	},
}));

const verticalSeparatorStyles = xcss({
	borderLeft: `${token('border.width.selected')} solid ${token('color.border')}`,
	height: '16px',
	margin: 'space.075',
});

const rightSideHeaderStyles = xcss({
	marginLeft: 'auto',
	alignItems: 'center',
});

const VerticalSeparator = () => <Box xcss={verticalSeparatorStyles} />;

SpacedActionItem.displayName = 'SpacedActionItem';

const EXCLUDED_SYSTEM_IDS = [
	CONTENT_METADATA_PAGE_RESTRICTIONS_ID,
	CONTENT_METADATA_ATTACHMENTS_ID,
];

const SYSTEM_IDS_TO_SHOW_WITH_PAGE_BUTTONS = [CONTENT_METADATA_PAGE_RESTRICTIONS_ID];

type MetadataProps = {
	shouldDisplayClassification: boolean;
};

type RenderContentHeaderProps = {
	contentType: string | null | undefined;
	contentTitle: string | null | undefined;
	isFabricPage: boolean;
	loading: boolean;
	subType: string | null | undefined;
	lastModifiedDate: string | null | undefined;
	isThemed: boolean;
	isContentToolsForcedOpen: boolean;
	setIsContentToolsForcedOpen: Dispatch<SetStateAction<boolean>>;
	isArchived: boolean;
};

type RenderBreadcrumbsProps = {
	contentTitle: string | null | undefined;
};

type ContentHeaderProps = {
	spaceKey: string;
	contentId: string;
	actionItems?: ComponentType<any>[];
	versionOverride?: number;
	className?: string;
	showMetadataItems?: boolean;
};

export const ContentHeader: FC<ContentHeaderProps> = ({
	spaceKey,
	contentId,
	actionItems = [],
	versionOverride = null,
	showMetadataItems = true,
}) => {
	const [isContentToolsForcedOpen, setIsContentToolsForcedOpen] = useState<boolean>(false);

	const { isExternalCollaborator } = useIsExternalCollaborator();
	const contentClassificationMetadata = useContentClassification({
		contentId,
		spaceKeyOverride: spaceKey,
		contentStatusContext: 'current-only',
	});
	const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();
	const isNav4Enabled = useIsNav4Enabled();

	const { shouldDisplayClassification } = contentClassificationMetadata;

	const leftRightPaddingOffset = -37;
	const widthLimitForMetadata = 1000;

	const { width } = usePageHeightWidthObserver(false, leftRightPaddingOffset, false);

	const renderBreadcrumbs = ({ contentTitle }: RenderBreadcrumbsProps) => {
		const shouldRenderMetadataForPageWidth = () => {
			return width > widthLimitForMetadata;
		};

		return (
			<Flex alignItems="center" xcss={breadcrumbsAreaWrapper} testId="breadcrumb">
				<Breadcrumbs contentId={contentId} spaceKey={spaceKey} contentTitle={contentTitle || ''} />
				<Inline alignBlock="center">
					{showMetadataItems && shouldRenderMetadataForPageWidth() && (
						<Metadata
							data-testid="metadata-wrapper"
							shouldDisplayClassification={shouldDisplayClassification}
						>
							<SystemContentMetadata
								key={`metadata-${contentId}`}
								contentId={contentId}
								versionOverride={versionOverride}
								excludeIds={EXCLUDED_SYSTEM_IDS}
							/>
							<PageMetadataBanner
								key={`banner-${contentId}`}
								contentId={contentId}
								versionOverride={versionOverride}
							/>
						</Metadata>
					)}
				</Inline>
			</Flex>
		);
	};

	const renderContentHeader = ({
		contentType,
		contentTitle,
		loading,
		subType,
		lastModifiedDate,
		isContentToolsForcedOpen,
		setIsContentToolsForcedOpen,
		isArchived,
		isFabricPage,
	}: RenderContentHeaderProps) => {
		return (
			<Fragment>
				{renderBreadcrumbs({
					contentTitle,
				})}
				<Box testId="content-classification-tag">
					<ContentClassificationTagLoader
						contentId={contentId}
						contentType={contentType || null}
						contentClassificationMetadata={contentClassificationMetadata}
						isEditMode={false}
					/>
				</Box>
				{showMetadataItems && <PageMetadataAtl contentId={contentId} />}

				<Flex alignItems="center" xcss={rightSideHeaderStyles}>
					{!isArchived && (
						<Flex alignItems="center">
							<ContentHeaderPresenceLoader
								contentId={contentId}
								contentType={contentType}
								analyticSource="viewPageScreen"
							/>
							<VerticalSeparator />
						</Flex>
					)}
					{!fg('cc_page_experiences_new_editor_byline') ? (
						<RendererContentState contentId={contentId} isDraft={false} />
					) : null}
					<GroupedPageButtons
						contentId={contentId}
						version={versionOverride}
						includeSystemIds={SYSTEM_IDS_TO_SHOW_WITH_PAGE_BUTTONS}
						spaceKey={spaceKey}
						contentType={contentType}
						contentSubType={subType}
						isExternalCollaborator={isExternalCollaborator}
						lastModifiedDate={lastModifiedDate}
						showEdit={!(isLivePagesFeatureEnabled && subType === 'live')}
						isContentToolsForcedOpen={isContentToolsForcedOpen}
						setIsContentToolsForcedOpen={setIsContentToolsForcedOpen}
						isArchived={isArchived}
						isFabricPage={isFabricPage}
					/>
				</Flex>

				{actionItems?.map((ActionItem: ComponentType<any>, index) => {
					const actionItemProps = {
						contentId,
						spaceKey,
						contentType,
					};
					return (
						<SpacedActionItem
							data-testid="spacedActionItemTestId"
							key={ActionItem?.displayName || index}
						>
							<ActionItem {...actionItemProps} disabled={loading} />
						</SpacedActionItem>
					);
				})}
				<ExperienceSuccess
					name={VIEW_PAGE_CONTENT_HEADER_EXPERIENCE}
					attributes={{ contentId, contentType, subType, isFabricPage, isContentWrapper: false }}
				/>
			</Fragment>
		);
	};

	return (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			{showMetadataItems && (
				<PageSegmentLoadStart
					key={`PageSegmentLoadStart-connect-${contentId}`}
					metric={CONNECT_APP_WEB_FRAGMENT_METRIC}
				/>
			)}
			{fg('confluence_frontend_content_wrapper') ? (
				<ExperienceStart
					key={`object-header-${contentId}`}
					id={contentId}
					name={OBJECT_HEADER_EXPERIENCE}
				/>
			) : (
				<ExperienceStart
					key={`view-page-header-${contentId}`}
					id={contentId}
					name={VIEW_PAGE_CONTENT_HEADER_EXPERIENCE}
				/>
			)}
			<Query<ContentHeaderUnifiedQueryType>
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				query={ContentHeaderUnifiedQuery}
				variables={{
					contentId,
					spaceKey,
					// Don't omit versionOverride, embeddedContentRender and limit
					// It's an Apollo bug that if preloader these needs to be match preloaded query
					// Also doesn't work if removing them from preloader variables HOT-109035
					versionOverride: versionOverride || null,
					embeddedContentRender: null,
					limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
					useNewContentTopper: true,
				}}
				context={{ single: true }}
			>
				{({ loading, error, data }) => {
					if (error) {
						if (isUnauthorizedError(error)) {
							markErrorAsHandled(error);
							return null;
						} else {
							// TODO: unknown errors should have a nicer UI within `<ErrorDisplay>`, but we don't have such UI for now
							return <ErrorDisplay error={error} />;
						}
					} else if (!data) {
						return null;
					}

					const contentNode = data?.content?.nodes?.[0];
					const contentType = contentNode?.type;
					const lastModifiedDate = contentNode?.metadata?.lastModifiedDate;
					const subType = contentNode?.subType;
					const contentTitle = contentNode?.title;
					const isFabricPage = checkIsFabricPage(contentNode);
					const spaceIsThemed = Boolean(data?.space?.theme?.themeKey);
					const isArchived = contentNode?.status === 'archived';

					const contentHeader =
						isFabricPage && fg('confluence_frontend_content_wrapper') ? (
							<ContentTypesHeader
								contentId={contentId}
								isConvertToFolderHighlighted={isContentToolsForcedOpen}
								setIsConvertToFolderHighlighted={setIsContentToolsForcedOpen}
							/>
						) : (
							renderContentHeader({
								contentType,
								contentTitle,
								isFabricPage,
								loading,
								subType,
								lastModifiedDate,
								isThemed: spaceIsThemed,
								isContentToolsForcedOpen,
								setIsContentToolsForcedOpen,
								isArchived,
							})
						);

					const shouldShowAifcCreateBanner =
						// eslint-disable-next-line confluence-feature-gating/no-preconditioning
						isFabricPage && fg('aifc_create_enabled') && fg('confluence_frontend_content_wrapper');

					return (
						<FixedSpaceHeaderContainer
							isThemed={spaceIsThemed}
							data-testid="content-header-container"
							shouldHavePadding={!isFabricPage} // tiny pages need extra left and right padding in header since we show the old header for tiny pages
							isNav4Enabled={isNav4Enabled}
							isObjectHeaderEnabled={fg('confluence_frontend_content_wrapper')}
							shouldShowAifcCreateBanner={shouldShowAifcCreateBanner}
						>
							{shouldShowAifcCreateBanner ? (
								<Stack grow="fill">
									{contentHeader}
									<SmartCreateFeedbackBannerLoader contentType="page" />
								</Stack>
							) : (
								contentHeader
							)}
						</FixedSpaceHeaderContainer>
					);
				}}
			</Query>
		</ErrorBoundary>
	);
};
